import { BoardID } from 'BoardID';
import {
  MaterialPatternString,
  SizeOnlyPatternString,
  SubBoardPatternString,
} from 'Pattern';
import { XYCoord } from 'react-dnd';

export type Rectangle = {
  height: number;
  width: number;
  top: number;
  left: number;
};

export type NormalizedRotation = 0 | 90 | 180 | 270;

export type RenderableMetadata<T extends BoardType> = {
  renderableIndex: number;
} & (T extends BoardType.Chevron
  ? {
      fragmentIndex: number;
      rowIndex: number;
    }
  : T extends BoardType.Pinwheel
  ? {
      fragmentIndex: number;
      rowIndex: number;
      colIndex: number;
    }
  : T extends BoardType.Plaid
  ? {
      rowIndex: number;
      colIndex: number;
      type: 'background' | 'wood1' | 'wood2' | 'hStripe' | 'vStripe';
    }
  : T extends BoardType.QuarterSquare
  ? {
      rowIndex: number;
      colIndex: number;
    }
  : T extends BoardType.Strips
  ? {
      fragmentIndex: number;
      rowIndex: number;
    }
  : T extends BoardType.StripsAdvanced
  ? {
      subpatternIndex: number;
      fragmentIndex: number;
      rowIndex: number;
    }
  : T extends BoardType.Weave
  ? {
      fragmentIndex: number | 'background';
    }
  : T extends BoardType.ZigZag
  ? {
      fragmentIndex: number;
      rowIndex: number;
      colIndex: number;
    }
  : never);

export type Renderable<T extends BoardType = BoardType> = {
  rect: Rectangle;
  material: Material | null;
  rotate?: number;
  selectionSizeProp: 'height' | 'width' | 'both';
  metadata: RenderableMetadata<T>;
};

export type ParamsPanelSelection<T extends BoardType> =
  T extends BoardType.Chevron
    ? { renderableIndex: number }
    : T extends BoardType.Pinwheel
    ? { renderableIndex: number }
    : T extends BoardType.Plaid
    ? { renderableIndex: number }
    : T extends BoardType.QuarterSquare
    ? null
    : T extends BoardType.Strips
    ? { renderableIndex: number }
    : T extends BoardType.StripsAdvanced
    ? { renderableIndex: number; subpatternIndex: number }
    : T extends BoardType.Weave
    ? { renderableIndex: number }
    : T extends BoardType.ZigZag
    ? { renderableIndex: number }
    : never;

export enum Material {
  A = 'A',
  B = 'B',
  BL = 'BL',
  BU = 'BU',
  BY = 'BY',
  C = 'C',
  CH = 'CH',
  J = 'J',
  M = 'M',
  P = 'P',
  PH = 'PH',
  S = 'S',
  W = 'W',
  WE = 'WE',
  YH = 'YH',
  Z = 'Z',
}

export const Materials = Object.keys(Material) as Array<Material>;

export enum Units {
  IN = 'in',
  MM = 'mm',
}

export enum Direction {
  H,
  V,
}

export type Coord = { top: number; left: number; isScaled: false };
export function CoordCtr({ top, left }: { top: number; left: number }): Coord {
  return { top, left, isScaled: false };
}

export type ScaledCoord = { top: number; left: number; isScaled: true };
export function ScaledCoordCtr({
  top,
  left,
}: {
  top: number;
  left: number;
}): ScaledCoord {
  return { top, left, isScaled: true };
}

export type Dimensions2D = {
  height: number;
  width: number;
};

export type Dimensions3D = {
  length: number;
  width: number;
  thickness: number;
};

export enum Theme {
  System,
  Light,
  Dark,
}

export type Settings = {
  theme: Theme;
  kerf: number;
  planeBuffer: number;
  units: Units;
  waste: number;
  metric: boolean;
  pxPerInch: number;
  random: {
    styles: OneArray<BoardType>;
    materials: TwoArray<Material>;
    size: Dimensions2D;
  };
  leftNavCollapsed: boolean;
  presetsCollapsed: boolean;
  customCollapsed: boolean;
  materialsCollapsed: boolean;
  detailsCollapsed: boolean;
  materialCosts: Record<
    Material,
    { price: number; boardUnit: 'fbm'; costUnit: 'USD' }
  >;
};

export type BoardDetails = {
  idealStartingStock: Array<{
    label?: string;
    stock: BoardDetailsStock;
  }>;
};

export type BoardDetailsStock = Array<{
  material: Material | null;
  dimensions: Dimensions3D;
}>;

// Boards

export enum BoardType {
  Chevron = 'Chevron',
  Pinwheel = 'Pinwheel',
  QuarterSquare = 'QuarterSquare',
  Plaid = 'Plaid',
  Strips = 'Strips',
  StripsAdvanced = 'StripsAdvanced',
  Weave = 'Weave',
  ZigZag = 'ZigZag',
}

export const BoardTypes = Object.keys(BoardType) as TwoArray<BoardType>;

export type BoardHash = string;

export type SneezeGuardState<T extends BoardType> = {
  selection: null | RenderableMetadata<T>;
  dragDelta: null | XYCoord;
};

export enum UserProfileType {
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
}

export type UserProfile = {
  type: UserProfileType;
  id: string;
  name: string;
  image: string;
  accessToken: string;
};

export type AccessTokens = {
  google: string | null;
  facebook: string | null;
};

export type RouteParams = Readonly<{
  boardID: BoardID;
  boardHash: string;
}>;

export type ParamsPanelProps<T extends BoardType> = {
  params: BoardParams<T>;
  onParamChange: <K extends keyof BoardParams<T>, V extends BoardParams<T>[K]>(
    key: K
  ) => (value: V) => void;
  selection: null | ParamsPanelSelection<T>;
  onSelectionChange: (selection: null | ParamsPanelSelection<T>) => void;
  units: Units;
};

export type OneArray<T> = [T, ...Array<T>];
export type TwoArray<T> = [T, T, ...Array<T>];

export type Bumps =
  | {
      bump: 0.125;
      shiftBump: 1;
    }
  | {
      bump: 1;
      shiftBump: 10;
    };

export type BoardParams<T extends BoardType> = T extends BoardType.Chevron
  ? ChevronParams
  : T extends BoardType.Pinwheel
  ? ZigZagParams
  : T extends BoardType.Plaid
  ? PlaidParams
  : T extends BoardType.QuarterSquare
  ? QuarterSquareParams
  : T extends BoardType.Strips
  ? StripsParams
  : T extends BoardType.StripsAdvanced
  ? StripsAdvancedParams
  : T extends BoardType.Weave
  ? WeaveParams
  : T extends BoardType.ZigZag
  ? ZigZagParams
  : never;

export type ChevronParams = {
  numRows: number;
  rowHeight: number;
  thickness: number;
  rotate: number;
  pattern: MaterialPatternString;
};

export type QuarterSquareParams = {
  wood1: Material;
  wood2: Material;
  pattern: SizeOnlyPatternString;
  thickness: number;
};

export type PinwheelParams = {
  numHorizontal: number;
  numVertical: number;
  pattern: MaterialPatternString;
  thickness: number;
};

export type PlaidParams = {
  background: Material;
  wood1: Material;
  wood2: Material;
  startOn: 'background' | 'wood1/background' | 'wood1/wood2' | 'wood2';
  colWidth: number;
  rowHeight: number;
  numCols: number;
  numRows: number;
  hStripe: MaterialPatternString;
  vStripe: MaterialPatternString;
  isHStripeOn1: boolean;
  isHStripeOn2: boolean;
  isVStripeOn1: boolean;
  isVStripeOn2: boolean;
};

export type StripsParams = {
  flip: boolean;
  numRows: number;
  pattern: MaterialPatternString;
  rowHeight: number;
  thickness: number;
};

export type StripsAdvancedParams = {
  pattern: SubBoardPatternString;
  subpatterns: Array<MaterialPatternString>;
  thickness: number;
};

export type WeaveParams = {
  pattern: MaterialPatternString;
  horizontalSize: number;
  verticalSize: number;
  thickness: number;
  background: MaterialPatternString;
};

export type ZigZagParams = {
  numHorizontal: number;
  numVertical: number;
  pattern: MaterialPatternString;
  thickness: number;
};
